
import { Component, Prop } from "vue-property-decorator";
import { mixins } from "vue-class-component";

// GraphQL
import GET_TEAM from "@/apollo/teams/GetTeam.gql";
import UPDATE_TEAM from "@/apollo/teams/UpdateTeam.gql";

import {
  Company,
  User,
  Team,
} from "@/generated/graphql";

import GetRelativeRouteNameMixin from "@/mixins/route/GetRelativeRouteNameMixin.vue";
// Components
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import RouteNames from "@/entities/RouteNames";
import TabsWithRoutes from "@/components/common/TabsWithRoutesNew.vue";
import RouteTabs from "@/entities/RouteTabs";
import GroupHeader from "@/components/teams/GroupHeader.vue";
import Roles from "@/entities/Roles";
import { NOTIFICATION } from "@/entities/Events";
import Message from "@/data/notificationsMessages";


interface ItemChipsData {
  id: string;
  name: string;
  role?: string;
}

@Component({
  components: { 
    LoadingOverlay,
    TabsWithRoutes,
    GroupHeader,
  },
  apollo: {
    group: {
      query: GET_TEAM,
      variables() {
        return {
          id: this.$route.params.id,
        };
      },
      update: (data) => data.team_by_pk,
    },
  },
})
export default class Group extends mixins(GetRelativeRouteNameMixin) {
  @Prop({type: Object, required: true}) readonly company!: Company;
  @Prop({type: Object, required: true}) readonly currentUser!: User;

  group: Team | undefined = undefined;

  routeTabs: RouteTabs = {
    [this.getRelativeRouteName(RouteNames.GROUP_DETAILS)]: "details",
    [this.getRelativeRouteName(RouteNames.GROUP_DOCUMENTS)]: "documents",
  };

  get groupId(): string | undefined {
    if (!this.group) {
      return;
    }
    return this.group.id;
  }

  get contractors(): ItemChipsData[] {
    if (!this.group || !this.group.contractors) {
      return [];
    }
    return this.group.contractors.map(({contractor}: any) => {
      return {
        id: contractor.id,
        name: `${contractor.user.given_name} ${contractor.user.family_name}`,
        role: Roles.CONTRACTOR,
        deleted: contractor.deleted,
      };
    })
  }

  get managers(): ItemChipsData[] {
    if (!this.group || !this.group.managers) {
      return [];
    }
    return this.group.managers.map(({manager}: any) => {
      return {
        id: manager.id,
        name: `${manager.user.given_name} ${manager.user.family_name}`,
        role: Roles.MANAGER
      }
    })
  }
  get skills(): ItemChipsData[] {
    if (!this.group || !this.group.skills) {
      return [];
    }
    return this.group.skills.map(({skill}: any) => {
      return {
        id: skill.id,
        name: skill.name,
      }
    })
  }

  get projects(): ItemChipsData[] {
    if (!this.group || !this.group.projects) {
      return [];
    }
    return this.group.projects.map(({project}: any) => {
      return {
        id: project.id,
        name: project?.client_name,
      }
    })
  }

  get postings(): ItemChipsData[] {
    if (!this.group || !this.group.postings) {
      return [];
    }
    return this.group.postings.map(({posting}: any) => {
      return {
        id: posting.id,
        name: posting.job_title,
      }
    })
  }

  async handleGroupArchived(): Promise<void> {
    this.$confirm({
      title: "Delete Team",
      message: `Are you sure you want to archive ${this.group?.name}`,
      button: {
        no: "No",
        yes: "Yes",
      },
      callback: async (confirm: any) => {
        if (confirm) {
          try {
            await this.softDeleteGroup();
            this.$eventBus.emit(NOTIFICATION, Message.successMessage(`${this.group?.name} has been archived`));
            this.$router.replace({ name: RouteNames.MANAGE_TEAMS});
          } catch (e) {
            this.$eventBus.emit(NOTIFICATION, Message.errorMessage(e.message));
          }
        }
      },
    });
  }

  handleGroupActivated(): void {
    try {
      this.activateGroup();
      this.$eventBus.emit(NOTIFICATION, Message.successMessage(`${this.group?.name} has been activated`));
    } catch (e) {
      this.$eventBus.emit(NOTIFICATION, Message.errorMessage(e.message));
    }
  }

  async softDeleteGroup() {
    if (!this.group || !this.group.archived == false) {
      return;
    }
    const set = {
      archived: true,
    };

    return await this.$apollo.mutate({
      mutation: UPDATE_TEAM,
      variables: {
        set,
        id: this.group.id,
      },
    });
  }

  async activateGroup() {
    if (!this.group || !this.group.archived == true) {
      return;
    }
    const set = {
      archived: false,
    };

    return await this.$apollo.mutate({
      mutation: UPDATE_TEAM,
      variables: {
        set,
        id: this.group.id,
      },
    });
  }
}
